import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PortalButton, Text, Modal } from '@custom-components';
import { stripGraphqlErrorPrefix } from '@lib/utils';
import { formatGuestName } from '@lib/guests';

function NavCustomerMemberEditModal(props) {
  const {
    member,
    organizationId,
    createToast,
    router,
    refetch,
    sendForgotPasswordEmail,
    deactivateMember,
    removeMember
  } = props;
  const { identityId, firstName, lastName, emailAddress, active } = member ?? {};

  const [openCustomerMemberModal, setOpenCustomerMemberModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', contentCopy: '', onClick: () => {}, warning: false });
  const [loading, setLoading] = useState(false);

  const handlePasswordReset = async () => {
    try {
      await sendForgotPasswordEmail({ variables: { emailAddress, sendEmail: true } });
      createToast(`A password reset email will be sent to ${emailAddress}`, { variant: 'success' });
    } catch (error) {
      const { message = 'An unexpected error occurred, unable to reset password.' } = error;
      createToast(stripGraphqlErrorPrefix(message), { variant: 'error' });
    }
  };

  const handleDeactivateMember = async (activate) => {
    try {
      await deactivateMember({ variables: { memberId: [identityId], activate } });
      await refetch();
      const action = activate ? 'activated' : 'deactivated';
      createToast(`${`${firstName} ${lastName}`} has been ${action}.`, { variant: 'success' });
    } catch (error) {
      const { message = 'An unexpected error occurred, unable to deactivate member.' } = error;
      createToast(stripGraphqlErrorPrefix(message), { variant: 'error' });
    }
  };

  const handleRemoveMember = async () => {
    try {
      await removeMember({ variables: { memberId: identityId, organizationId } });
      router.back();
      createToast(`${`${firstName} ${lastName}`} has been removed from this organization.`, { variant: 'success' });
    } catch (error) {
      const { message = 'An unexpected error occurred, unable to remove member.' } = error;
      createToast(stripGraphqlErrorPrefix(message), { variant: 'error' });
    }
  };

  return (
    <>
      <PortalButton
        label="Reset Password"
        onClick={() => {
          setModalContent({
            title: 'Reset Password',
            contentCopy: `Are you sure you would like to reset the password for "${formatGuestName({
              firstName,
              lastName
            })}"?`,
            onClick: async () => {
              setLoading(true);
              await handlePasswordReset();
              setLoading(false);
            },
            warning: true
          });
          setOpenCustomerMemberModal(true);
        }}
      />
      <PortalButton
        label={active ? 'Deactivate Member' : 'Activate Member'}
        onClick={() => {
          setModalContent({
            title: `${active ? 'Deactivate' : 'Activate'} Member`,
            contentCopy: `Are you sure you would like to ${active ? 'deactivate' : 'activate'} "${formatGuestName({
              firstName,
              lastName
            })}"?`,
            onClick: async () => {
              setLoading(true);
              await handleDeactivateMember(!active);
              setLoading(false);
            }
          });
          setOpenCustomerMemberModal(true);
        }}
      />
      <PortalButton
        label="Remove Member"
        onClick={() => {
          setModalContent({
            title: 'Remove Member',
            contentCopy: `Are you sure you would like to remove "${formatGuestName({
              firstName,
              lastName
            })}"?`,
            onClick: async () => {
              setLoading(true);
              await handleRemoveMember();
              setLoading(false);
            },
            warning: true
          });
          setOpenCustomerMemberModal(true);
        }}
      />
      <Modal
        openModal={openCustomerMemberModal}
        handleClose={() => {
          setOpenCustomerMemberModal(false);
        }}
        title={modalContent.title}
        actions={[
          {
            label: 'cancel',
            onClick: () => {
              setOpenCustomerMemberModal(false);
            },
            btnTheme: 'blank',
            disabled: loading
          },
          {
            label: 'Confirm',
            type: 'submit',
            onClick: async () => {
              await modalContent.onClick();
              setOpenCustomerMemberModal(false);
            },
            btnTheme: 'quaternary',
            disabled: loading,
            loading
          }
        ]}
        content={
          <>
            <Text.CopyBody>{modalContent.contentCopy}</Text.CopyBody>
            {modalContent.warning && <Text.CopyBody weight="bold">This action cannot be undone.</Text.CopyBody>}
          </>
        }
      />
    </>
  );
}

NavCustomerMemberEditModal.propTypes = {
  member: PropTypes.shape({ firstName: PropTypes.string, lastName: PropTypes.string, email: PropTypes.string })
    .isRequired,
  createToast: PropTypes.func.isRequired,
  organizationId: PropTypes.number.isRequired,
  sendForgotPasswordEmail: PropTypes.func.isRequired,
  deactivateMember: PropTypes.func.isRequired,
  removeMember: PropTypes.func.isRequired,
  router: PropTypes.shape({ back: PropTypes.func }).isRequired,
  refetch: PropTypes.func.isRequired
};

export default NavCustomerMemberEditModal;
